import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Page d'aide « Comment utiliser TicketFlow » — guide A→Z.
 * Accessible publiquement sur /how-to-use.
 * @author Dr Hamid MADANI <drmdh@msn.com>
 */

const SECTIONS = [
  {
    id: 'apercu',
    title: '1. Aperçu du système',
    body: (
      <>
        <p>TicketFlow est un système de gestion de file d'attente composé de quatre applications qui dialoguent en temps réel (Socket.io) autour d'un serveur central :</p>
        <ul className="list-disc ml-6 mt-2 space-y-1">
          <li><strong>Serveur</strong> — cœur du système (API + temps réel + base de données). Sert aussi les écrans <code>/display</code> et l'administration <code>/admin</code>.</li>
          <li><strong>Distributeur</strong> (kiosque) — le visiteur prend un ticket pour un service ; le ticket est imprimé.</li>
          <li><strong>Guichet</strong> (agent) — l'agent appelle, sert et termine les tickets.</li>
          <li><strong>Affichage</strong> (<code>/display</code>) — écran de salle d'attente : tickets appelés, guichets, nombre en attente par service, annonces vocales.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'installation',
    title: '2. Installation',
    body: (
      <>
        <p>Installez d'abord le <strong>serveur</strong> (un seul, sur le poste central), puis les clients sur les autres postes.</p>
        <ol className="list-decimal ml-6 mt-2 space-y-1">
          <li>Lancez <code>TicketFlow-Serveur-x.x.x-Setup.exe</code> et suivez l'assistant.</li>
          <li>Au premier démarrage, l'assistant <code>/setup</code> crée le compte administrateur et la première agence.</li>
          <li>Installez ensuite <code>TicketFlow-Distributeur</code> et <code>TicketFlow-Guichet</code> sur leurs postes respectifs.</li>
        </ol>
        <p className="mt-2">Les exécutables sont autonomes (aucune dépendance à installer).</p>
      </>
    ),
  },
  {
    id: 'reseau',
    title: '3. Configuration réseau',
    body: (
      <>
        <p>Tous les postes doivent être sur le <strong>même réseau local</strong> que le serveur.</p>
        <ul className="list-disc ml-6 mt-2 space-y-1">
          <li>Le serveur écoute sur le port <strong>5000</strong>.</li>
          <li>Sur les clients, l'adresse du serveur est <code>http://&lt;IP-du-serveur&gt;:5000</code> (ex. <code>http://192.168.1.12:5000</code>).</li>
          <li>Ouvrez le port <strong>5000</strong> dans le pare-feu Windows du poste serveur (entrant, TCP).</li>
        </ul>
        <p className="mt-2 text-amber-700">Astuce : trouvez l'IP du serveur avec <code>ipconfig</code> (carte réseau active).</p>
      </>
    ),
  },
  {
    id: 'demarrage',
    title: '4. Démarrer le serveur',
    body: (
      <>
        <p>Lancez l'application <strong>Serveur</strong>. Elle ouvre automatiquement l'écran d'affichage et expose :</p>
        <ul className="list-disc ml-6 mt-2 space-y-1">
          <li><code>/display</code> — écran d'affichage (salle d'attente) ;</li>
          <li><code>/admin</code> — administration (connexion requise) ;</li>
          <li><code>/distributor</code> et <code>/counter</code> — versions web du distributeur et du guichet.</li>
        </ul>
        <p className="mt-2">Vérifiez l'état avec <code>http://localhost:5000/api/health</code> (doit répondre <code>ok</code>).</p>
      </>
    ),
  },
  {
    id: 'distributeur',
    title: '5. Le distributeur (prise de ticket)',
    body: (
      <>
        <ol className="list-decimal ml-6 space-y-1">
          <li>Au premier lancement, ouvrez « Configuration Serveur » et saisissez l'URL <code>http://&lt;IP&gt;:5000</code>.</li>
          <li>Authentifiez le poste si demandé.</li>
          <li>Le visiteur touche un service → un ticket est créé et <strong>imprimé</strong>.</li>
        </ol>
        <p className="mt-2"><strong>Impression :</strong> dans la configuration d'impression, activez l'impression automatique et choisissez l'imprimante. Chaque distributeur n'imprime que <em>les tickets qu'il émet lui-même</em> (un seul ticket par prise).</p>
      </>
    ),
  },
  {
    id: 'guichet',
    title: '6. Le guichet (agent)',
    body: (
      <>
        <ol className="list-decimal ml-6 space-y-1">
          <li>Connectez le poste au serveur (URL + port) puis ouvrez une session agent.</li>
          <li>Sélectionnez votre guichet et le(s) service(s) traité(s).</li>
          <li>Utilisez <strong>Appeler le suivant</strong>, <strong>Rappeler</strong>, <strong>Mettre en pause</strong>, <strong>Terminer</strong>.</li>
        </ol>
        <p className="mt-2">Chaque appel met à jour l'écran d'affichage et déclenche l'annonce vocale.</p>
      </>
    ),
  },
  {
    id: 'affichage',
    title: '7. L\'écran d\'affichage (/display)',
    body: (
      <>
        <ul className="list-disc ml-6 space-y-1">
          <li>Affiche les tickets appelés, le guichet correspondant et le <strong>nombre en attente</strong> par service (carrés).</li>
          <li>Passe en <strong>plein écran</strong> automatiquement après ~10 s d'inactivité (sortie : souris/Échap). En mode kiosque/Electron, le plein écran est garanti.</li>
          <li><strong>Son & voix</strong> : carillon d'appel + annonce vocale bilingue FR/AR (synthèse vocale du système). Installez une voix arabe sur l'OS de l'écran pour la lecture en arabe.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'admin',
    title: '8. Administration (/admin)',
    body: (
      <>
        <p>Connectez-vous sur <code>/admin</code>. Onglets principaux :</p>
        <ul className="list-disc ml-6 mt-2 space-y-1">
          <li><strong>Agence</strong> — nom, logo, messages d'accueil (FR/AR).</li>
          <li><strong>Services</strong> — types de services (code, nom, couleur).</li>
          <li><strong>Guichets</strong> — postes et services associés.</li>
          <li><strong>Affichage</strong> — options de l'écran (afficher la file, durées, rotation, audio).</li>
          <li><strong>Styles</strong> — thèmes de couleurs et apparence des carrés (voir §9).</li>
          <li><strong>Imprimante</strong>, <strong>Paramètres avancés</strong>, <strong>Utilisateurs</strong>, <strong>Historique</strong>, <strong>Statistiques</strong>, <strong>Publicité</strong>.</li>
        </ul>
        <p className="mt-2">N'oubliez pas <strong>Enregistrer les modifications</strong> en bas de page.</p>
      </>
    ),
  },
  {
    id: 'themes',
    title: '9. Thèmes & apparence',
    body: (
      <>
        <p>Deux mécanismes complémentaires, dans <strong>/admin → Styles</strong> :</p>
        <p className="mt-2 font-semibold">a) Thèmes de couleurs (« Gérer les Thèmes »)</p>
        <ul className="list-disc ml-6 space-y-1">
          <li>8 thèmes prédéfinis (Défaut, Sombre, Accessible, Moderne, Havana, Australie, Jakarta, Paris). <strong>Chaque thème garde ses propres couleurs.</strong></li>
          <li><strong>Activer</strong> un thème l'applique partout ; <strong>Dupliquer</strong> crée une copie éditable ; <strong>Exporter / Importer</strong> en JSON.</li>
          <li>Modifier une couleur dans « Personnalisation des Styles » ne change que le <strong>thème actif</strong>.</li>
        </ul>
        <p className="mt-2 font-semibold">b) Carrés de services de l'écran /display</p>
        <ul className="list-disc ml-6 space-y-1">
          <li><strong>Forme</strong> : Rectangle, Ovale, Livre, Ticket cranté.</li>
          <li><strong>Typographie</strong> et <strong>taille</strong> (largeur, taille du nombre/nom, padding, colonnes).</li>
          <li>Ces réglages sont enregistrés dans le thème actif et appliqués sur <code>/display</code>.</li>
        </ul>
        <p className="mt-2 font-semibold">c) Thèmes graphiques (feuilles CSS)</p>
        <p>Habillage complet à base de fichiers CSS (ex. « Algérie Télécom »), activable dans la même section.</p>
      </>
    ),
  },
  {
    id: 'impression',
    title: '10. Impression des tickets',
    body: (
      <>
        <ul className="list-disc ml-6 space-y-1">
          <li>L'impression est réalisée <strong>côté client</strong> (distributeur), pas par le serveur.</li>
          <li>Configurez l'imprimante et l'impression automatique dans le distributeur.</li>
          <li>Le serveur fournit le rendu/format du ticket ; chaque poste imprime ses propres tickets.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'publicite',
    title: '11. Publicité (écran d\'affichage)',
    body: (
      <>
        <p>Dans <strong>/admin → Publicité</strong>, activez le diaporama, ajoutez des visuels et réglez la durée et la transition. Les diapositives s'affichent sur <code>/display</code> pendant les périodes d'inactivité.</p>
      </>
    ),
  },
  {
    id: 'demos',
    title: '12. Démos intégrées',
    body: (
      <>
        <p>Deux jeux de démonstration sont fournis (Mairie de Mostaganem, Yalidine). Ils se chargent via une base de démo dédiée (variable <code>DEMO_DB</code>).</p>
        <p className="mt-2 text-amber-700">Après mise à jour d'un <code>.exe</code>, pensez à purger l'ancienne base dans le dossier <code>userData</code> si vous repartez d'une démo.</p>
      </>
    ),
  },
  {
    id: 'depannage',
    title: '13. Dépannage (FAQ)',
    body: (
      <>
        <ul className="list-disc ml-6 space-y-1">
          <li><strong>Client « déconnecté »</strong> → vérifiez l'IP/port du serveur, qu'il est démarré, et le pare-feu (port 5000).</li>
          <li><strong>Deux tickets imprimés</strong> → un seul distributeur doit être « émetteur » ; vérifiez qu'aucune ancienne instance ne tourne en parallèle.</li>
          <li><strong>Carrés /display trop petits</strong> → /admin → Styles → « Carrés de services » : ajustez taille/forme.</li>
          <li><strong>Pas de voix arabe</strong> → installez une voix arabe dans les paramètres de synthèse vocale de l'OS de l'écran.</li>
          <li><strong>Plein écran non automatique dans un navigateur</strong> → un geste utilisateur est requis ; utilisez le mode Electron/kiosque.</li>
        </ul>
      </>
    ),
  },
];

const HowToUse = () => {
  return (
    <div className="min-h-screen bg-gray-100 text-gray-800">
      <header className="bg-primary-600 text-white px-6 py-5 shadow">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Guide d'utilisation TicketFlow</h1>
            <p className="opacity-90 text-sm">De A à Z — installation, exploitation, administration</p>
          </div>
          <Link to="/" className="text-sm underline opacity-90 hover:opacity-100">Retour à l'application</Link>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-6 grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Sommaire */}
        <nav className="md:col-span-1 md:sticky md:top-6 self-start bg-white rounded-lg shadow p-4 h-max">
          <h2 className="font-semibold mb-2 text-sm uppercase text-gray-500">Sommaire</h2>
          <ul className="space-y-1 text-sm">
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`} className="text-primary-600 hover:underline">{s.title}</a>
              </li>
            ))}
          </ul>
        </nav>

        {/* Contenu */}
        <main className="md:col-span-3 space-y-5">
          {SECTIONS.map((s) => (
            <section key={s.id} id={s.id} className="bg-white rounded-lg shadow p-5 scroll-mt-6">
              <h2 className="text-xl font-bold mb-3 text-primary-700">{s.title}</h2>
              <div className="leading-relaxed">{s.body}</div>
            </section>
          ))}

          <footer className="text-center text-xs text-gray-500 py-4">
            TicketFlow — Dr Hamid MADANI &lt;drmdh@msn.com&gt;
          </footer>
        </main>
      </div>
    </div>
  );
};

export default HowToUse;
